/**
 * API Service Layer - GET-based (CORS-friendly)
 * All requests use GET method to avoid CORS preflight issues
 */

// ⚠️ IMPORTANT: Replace this URL with your deployed Google Apps Script Web App URL
const API_BASE_URL = 'https://script.google.com/macros/s/AKfycbxIgKaTjjrzFzEE41YpQccQnPJpMiNXZin2h8YT50VBeX0LUmjkr3aewrNG9VoY2-HS/exec';

/**
 * Register a new user
 */
export const registerUser = async (fullName, email, whatsapp, password) => {
  try {
    const url = `${API_BASE_URL}?action=register&fullName=${encodeURIComponent(
      fullName
    )}&email=${encodeURIComponent(email)}&whatsapp=${encodeURIComponent(
      whatsapp
    )}&password=${encodeURIComponent(password)}`;

    const response = await fetch(url, {
      method: 'GET',
    });

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Registration API Error:', error);
    return {
      success: false,
      message: 'Network error. Please check your connection.',
    };
  }
};

/**
 * Login user
 */
export const loginUser = async (buyerId, password) => {
  try {
    const url = `${API_BASE_URL}?action=login&buyerId=${encodeURIComponent(
      buyerId
    )}&password=${encodeURIComponent(password)}`;

    const response = await fetch(url, {
      method: 'GET',
    });

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Login API Error:', error);
    return {
      success: false,
      message: 'Network error. Please check your connection.',
    };
  }
};

/**
 * Submit order (uses FormData to avoid CORS preflight)
 */
export const submitOrder = async (orderData) => {
  try {
    // Use URL parameters like GET, but with POST method
    // This avoids CORS preflight while allowing large data
    const params = new URLSearchParams({
      action: 'submitOrder',
      buyerId: orderData.buyerId,
      fullName: orderData.fullName,
      brandName: orderData.brandName,
      productName: orderData.productName,
      orderId: orderData.orderId,
      orderTotal: orderData.orderTotal,
      lessAmount: orderData.lessAmount || 0,
      screenshot: orderData.screenshot,
    });

    const response = await fetch(API_BASE_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: params.toString(),
    });

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Submit Order API Error:', error);
    return {
      success: false,
      message: 'Network error. Please check your connection.',
    };
  }
};

/**
 * Update order (for affiliate details after order is accepted)
 */
export const updateOrder = async (updateData) => {
  try {
    const params = new URLSearchParams({
      action: 'updateOrder',
      buyerId: updateData.buyerId,
      rowIndex: updateData.rowIndex,
      affiliateSS: updateData.affiliateSS || '',
      affiliateLink: updateData.affiliateLink || '',
      deliveredSS: updateData.deliveredSS || '',
      brandName: updateData.brandName || '',
      productName: updateData.productName || '',
      orderId: updateData.orderId || '',
      orderTotal: updateData.orderTotal || '',
      lessAmount: updateData.lessAmount || 0,
      screenshot: updateData.screenshot || '',
      fullName: updateData.fullName || '',
    });

    const response = await fetch(API_BASE_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: params.toString(),
    });

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Update Order API Error:', error);
    return {
      success: false,
      message: 'Network error. Please check your connection.',
    };
  }
};

/**
 * Get user orders
 */
export const getUserOrders = async (buyerId) => {
  try {
    const url = `${API_BASE_URL}?action=getOrders&buyerId=${encodeURIComponent(
      buyerId
    )}`;

    const response = await fetch(url, {
      method: 'GET',
    });

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Get Orders API Error:', error);
    return {
      success: false,
      message: 'Network error. Please check your connection.',
    };
  }
};

/**
 * Get available products/deals
 */
export const getProducts = async () => {
  try {
    const url = `${API_BASE_URL}?action=getProducts`;

    const response = await fetch(url, {
      method: 'GET',
    });

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Get Products API Error:', error);
    return {
      success: false,
      message: 'Failed to fetch products',
    };
  }
};

/**
 * Submit Contact Form
 */
export const submitContactForm = async (contactData) => {
  try {
    const params = new URLSearchParams({
      action: 'submitContact',
      name: contactData.name,
      brandName: contactData.brandName || '',
      email: contactData.email,
      contactNumber: contactData.contactNumber,
      message: contactData.message || '',
    });

    const url = `${API_BASE_URL}?${params.toString()}`;

    const response = await fetch(url, {
      method: 'GET',
    });

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Submit Contact Form API Error:', error);
    return {
      success: false,
      message: 'Network error. Please check your connection.',
    };
  }
};

/**
 * Convert file to base64
 */
export const fileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });
};

/**
 * Update user payment details
 */
export const updatePaymentDetails = async (buyerId, paymentData) => {
  try {
    const params = new URLSearchParams({
      action: 'updatePaymentDetails',
      buyerId: buyerId,
      bankName: paymentData.bankName || '',
      accountNumber: paymentData.accountNumber || '',
      ifscCode: paymentData.ifscCode || '',
      accountHolderName: paymentData.accountHolderName || '',
      upiId: paymentData.upiId || '',
    });

    const url = `${API_BASE_URL}?${params.toString()}`;

    const response = await fetch(url, {
      method: 'GET',
    });

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Update Payment Details API Error:', error);
    return {
      success: false,
      message: 'Network error. Please check your connection.',
    };
  }
};

/**
 * Get user payment details
 */
export const getPaymentDetails = async (buyerId) => {
  try {
    const url = `${API_BASE_URL}?action=getPaymentDetails&buyerId=${encodeURIComponent(
      buyerId
    )}`;

    const response = await fetch(url, {
      method: 'GET',
    });

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Get Payment Details API Error:', error);
    return {
      success: false,
      message: 'Network error. Please check your connection.',
    };
  }
};

/**
 * Check if Order ID exists
 */
export const checkDuplicateOrderId = async (orderId) => {
  try {
    const url = `${API_BASE_URL}?action=checkOrderId&orderId=${encodeURIComponent(
      orderId
    )}`;

    const response = await fetch(url, {
      method: 'GET',
    });

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Check Order ID API Error:', error);
    return {
      success: false,
      message: 'Network error. Please check your connection.',
    };
  }
};

/**
 * Check User Account Status
 */
export const checkUserStatus = async (buyerId) => {
  try {
    const url = `${API_BASE_URL}?action=checkUserStatus&buyerId=${encodeURIComponent(
      buyerId
    )}`;

    const response = await fetch(url, {
      method: 'GET',
    });

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Check User Status API Error:', error);
    // Don't block user on network error, assume success or handle gracefully
    return {
      success: false,
      message: 'Network error.',
    };

  }
};

/**
 * Submit KYC Verification
 */
export const submitKYC = async (kycData) => {
  try {
    const params = new URLSearchParams({
      action: 'submitKYC',
      buyerId: kycData.buyerId,
      fullName: kycData.fullName,
      idType: kycData.idType,
      idNumber: kycData.idNumber,
      document: kycData.document,
    });

    const response = await fetch(API_BASE_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: params.toString(),
    });

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Submit KYC API Error:', error);
    return {
      success: false,
      message: 'Network error. Please check your connection.',
    };
  }
};

/**
 * Check for duplicate KYC ID Number
 */
/**
 * Check for KYC Buyer ID Status
 */
export const checkKycBuyerId = async (buyerId) => {
  try {
    const url = `${API_BASE_URL}?action=checkKycBuyerId&buyerId=${encodeURIComponent(buyerId)}`;
    const response = await fetch(url);
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Check KYC Buyer ID Error:', error);
    return { success: false, message: 'Network error' };
  }
};

/**
 * Send OTP for password reset
 */
export const sendOTP = async (identifier) => {
  try {
    const url = `${API_BASE_URL}?action=sendOTP&identifier=${encodeURIComponent(identifier)}`;
    const response = await fetch(url, { method: 'GET' });
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Send OTP API Error:', error);
    return { success: false, message: 'Network error. Please check your connection.' };
  }
};

/**
 * Reset password using OTP
 */
export const resetPassword = async (identifier, otp, newPassword) => {
  try {
    const url = `${API_BASE_URL}?action=resetPassword&identifier=${encodeURIComponent(identifier)}&otp=${encodeURIComponent(otp)}&newPassword=${encodeURIComponent(newPassword)}`;
    const response = await fetch(url, { method: 'GET' });
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Reset Password API Error:', error);
    return { success: false, message: 'Network error. Please check your connection.' };
  }
};

/**
 * Cancel an order
 */
export const cancelOrder = async (cancelData) => {
  try {
    const params = new URLSearchParams({
      action: 'cancelOrder',
      buyerId: cancelData.buyerId,
      rowIndex: cancelData.rowIndex,
      reason: cancelData.reason || '',
    });

    const response = await fetch(API_BASE_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: params.toString(),
    });

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Cancel Order API Error:', error);
    return {
      success: false,
      message: 'Network error. Please check your connection.',
    };
  }
};

